import React, { useState } from "react";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  ACCOUNTANT,
  EDITED_SUCCESS_MSG,
  ERROR_MSG,
} from "../../../../core/data/constants/constant";
import { MODULES } from "../../../../core/data/constants/moduleConstants";
import { all_routes } from "../../../router/all_routes";
import { useStages } from "../../../crmSetting/stages/hooks/useStages";
import DealApi from "../api/DealApi";
import DealConfirmModal from "./DealConfirmModal";
import { useSelector } from "react-redux";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import {
  updateDealsListCache,
  updateSingleDealCache,
} from "../actions/updateDealsListCache";

const DealPipelineStatus = ({ dealData }) => {
  const route = all_routes;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state?.auth);
  const isEditableByAccountant = user?.role?.name === ACCOUNTANT;
  const urlParams = getAllUrlParams();

  const { filters } = useSelector((state) => state.filter);
  const dealsFilters = filters.deals || [];
  let { queryParams, params } = generateQueryParams(dealsFilters);

  const { stagesData, isLoading } = useStages({
    ofCompany: dealData?.ofCompany?._id,
    module: MODULES.DEALS,
  });

  const stages = stagesData?.data?.data;

  const { currentStatus: currentStage } = dealData || {};

  const mutationUpdate = useMutation({
    mutationFn: ({ id, updatedStage }) =>
      DealApi.updateDeal(id, {
        currentStatus: updatedStage,
        textMessageAboutActivity: `<b>${MODULES.DEALS.slice(
          0,
          -1
        )} currentStatus</b> updated from <b>${currentStage?.name}</b> to <b>${
          updatedStage?.name
        }</b>`,
      }),
    onMutate: async ({ id, updatedStage }) => {
      await queryClient.cancelQueries(["deal", id]);

      const previousDeal = queryClient.getQueryData(["deal", id]);

      updateSingleDealCache(dealData?._id, {
        ...previousDeal,
        currentStatus: updatedStage,
      });

      updateDealsListCache({
        id: dealData?._id,
        updatedData: { ...previousDeal, currentStatus: updatedStage },
        page: urlParams?.page * 1,
        limit: urlParams?.limit * 1,
        queryString: params?.toString(),
        queryParams: queryParams?.toString(),
        companyId: urlParams?.companyid,
      });

      return { previousDeal };
    },
    onSuccess: (response) => {
      toast.success(EDITED_SUCCESS_MSG);
      if (response?.status) {
        toast.success(response.msg);
        updateSingleDealCache(dealData?._id, response?.data);
        updateDealsListCache({
          id: dealData?._id,
          updatedData: response?.data,
          page: urlParams?.page * 1,
          limit: urlParams?.limit * 1,
          queryString: params?.toString(),
          queryParams: queryParams?.toString(),
          companyId: urlParams?.companyid,
        });
      }
    },
    onError: (error, _, context) => {
      toast.error(error.response?.data?.msg || ERROR_MSG);
      // ✅ Rollback to previous state on error
      if (context?.previousDeal) {
        queryClient.setQueryData(["deal", dealData?._id], context.previousDeal);
      }
    },
  });

  const handleStageChange = (stage) => {
    if (!dealData?.editable) {
      toast.warning(
        `You are not able to edit. Because deal is converted to ${dealData?.currentStatus?.name}`
      );
      return;
    } else if (stage?.id === currentStage?.id) {
      toast.warning("You are already on this stage!");
      return;
    }

    setSelectedStage(stage);

    // Open modal for dealWon or dealLost stages
    if (stage?.dealWon || stage?.dealLost) {
      setShowConfirmModal(true);
    } else {
      // Update directly for other stages
      mutationUpdate.mutate({
        id: dealData?._id,
        updatedStage: stage,
      });
    }
  };

  const handleConfirm = () => {
    // Update the stage after confirmation
    mutationUpdate.mutate({
      id: dealData?._id,
      updatedStage: selectedStage,
    });
    setShowConfirmModal(false);
  };

  return (
    <div className="col-md-12">
      <div className="row mb-3" style={{ margin: "0 2px" }}>
        <div className="card mb-3">
          <div className="card-body p-0 mt-2">
            <h5 className="fw-semibold mb-3">Deal Pipeline Status</h5>
            <div className="d-flex flex-wrap pipeline-list">
              {isLoading || mutationUpdate?.isPending
                ? // Render dummy stages when loading
                  Array(5)
                    .fill(null)
                    .map((_, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center stage-container position-relative "
                      >
                        <div
                          className="px-2 py-1 rounded-pill text-white 
                                     d-flex align-items-center mb-3 text-capitalize"
                          style={{
                            width: "5rem",
                            height: "2rem",
                            backgroundColor: "lightgray",
                            cursor: "default",
                            transition: "all 0.2s ease-in-out",
                          }}
                        ></div>
                        {index < 4 && (
                          <div
                            className="d-flex align-items-center h-100 position-relative"
                            style={{
                              transform: "translateY(-6px)",
                            }}
                          >
                            <HiOutlineArrowNarrowRight
                              size={25}
                              className="stage-arrow"
                              color="lightgray"
                            />
                          </div>
                        )}
                      </div>
                    ))
                : // Render actual stages when not loading
                  stages?.map((stage, index) => (
                    <div
                      key={stage?.id}
                      className="d-flex align-items-center stage-container position-relative "
                    >
                      <div
                        className={`px-2 py-1 rounded-pill text-white 
                                   d-flex align-items-center mb-3 text-capitalize ${
                                     stage?.id === currentStage?.id
                                       ? "shadow-lg active-stage"
                                       : "inactive-stage"
                                   }`}
                        style={{
                          backgroundColor: stage?.color,
                          cursor: isEditableByAccountant
                            ? "not-allowed"
                            : "pointer",
                          transition: "all 0.2s ease-in-out",
                        }}
                        onClick={
                          !isEditableByAccountant
                            ? () => handleStageChange(stage)
                            : undefined
                        }
                                 data-bs-toggle={`${stage?.postAction?.autoOpenReminder && "modal"}`}
                      data-bs-target={`${stage?.postAction?.autoOpenReminder ? "#setReminderModal" : ""} `}
                      >
                        {stage?.name}
                      </div>
                      {index < stages?.length - 1 && (
                        <div
                          className="d-flex align-items-center h-100 position-relative"
                          style={{
                            transform: "translateY(-6px)",
                          }}
                        >
                          <HiOutlineArrowNarrowRight
                            size={25}
                            className="stage-arrow"
                          />
                        </div>
                      )}
                    </div>
                  ))}
            </div>
          </div>
          <DealConfirmModal
            open={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={handleConfirm}
            selectedStage={selectedStage}
            dealData={dealData}
          />
        </div>
      </div>
    </div>
  );
};

export default DealPipelineStatus;
